import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  HardDrive, Download, FolderUp, Smartphone, Database,
  FolderOpen, Save, RefreshCw, CheckCircle, XCircle, Loader2,
  Cloud, CloudOff, Check, AlertCircle, FileText, ChevronDown,
  Users, Users2, Receipt, ClipboardCheck, Calendar, Package,
  Car, Clock, PenTool, FileCode, Repeat, MapPin, Tag, Layers,
  HardHat, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../components/ConfirmModal';
import { mergeRecordArrays, mergeSettings } from '../utils/mergeRecords';
import autoSync from '../services/AutoSync';
import { IconTile, TINT } from '../components/ui/IconTile';
import deviceStorage from '../services/DeviceStorageService';
import { useData } from '../contexts/DataContext';

const Storage = () => {
  const importFileRef = useRef(null);
  const { syncStatus, lastSyncTime, isOnline, syncEnabled, forceSync, forceLoadFromCloud, toggleSync } = useData();
  const [storageInfo, setStorageInfo] = useState({
    localStorageUsed: 0,
    indexedDBUsed: 0,
    lastBackup: null,
    totalItems: {}
  });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  // Device Storage State
  const [deviceStorageStatus, setDeviceStorageStatus] = useState({
    isSupported: false,
    isConnected: false,
    folderName: null,
    lastSave: null,
    hasStoredFolder: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [syncMode, setSyncMode] = useState('cloud'); // 'cloud' | 'folder' | 'file'
  const [showHelp, setShowHelp] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);

  // Friendly labels + icons + colors for each data type key in localStorage
  const DATA_META = {
    customers:            { label: 'Customers',            icon: Users2,          tint: '#ec4899' },
    employees:            { label: 'Employees',            icon: Users,           tint: '#a855f7' },
    crews:                { label: 'Crews',                icon: HardHat,         tint: '#8b5cf6' },
    appointments:         { label: 'Appointments',         icon: Calendar,        tint: '#f97316' },
    invoices:             { label: 'Invoices',             icon: Receipt,         tint: '#22c55e' },
    estimates:            { label: 'Estimates',            icon: FileText,        tint: '#0ea5e9' },
    quotes:               { label: 'Quotes',               icon: FileText,        tint: '#0ea5e9' },
    recurring_invoices:   { label: 'Recurring Invoices',   icon: Repeat,          tint: '#16a34a' },
    invoiceTemplates:     { label: 'Invoice Templates',    icon: FileCode,        tint: '#0d9488' },
    quoteTemplates:       { label: 'Quote Templates',      icon: FileCode,        tint: '#0d9488' },
    expenses:             { label: 'Expenses',             icon: Receipt,         tint: '#ef4444' },
    inspections:          { label: 'Inspections',          icon: ClipboardCheck,  tint: '#10b981' },
    inventory:            { label: 'Inventory',            icon: Package,         tint: '#8b5cf6' },
    inventory_locations:  { label: 'Inventory Locations',  icon: MapPin,          tint: '#8b5cf6' },
    inventory_categories: { label: 'Inventory Categories', icon: Tag,             tint: '#8b5cf6' },
    mileage:              { label: 'Mileage',              icon: Car,             tint: '#0ea5e9' },
    mileageEntries:       { label: 'Mileage Entries',      icon: Car,             tint: '#0ea5e9' },
    mileage_trips:        { label: 'Mileage Trips',        icon: Car,             tint: '#0ea5e9' },
    timeEntries:          { label: 'Time Entries',         icon: Clock,           tint: '#3b82f6' },
    signatures:           { label: 'Signatures',           icon: PenTool,         tint: '#f59e0b' },
    sync_queue:           { label: 'Sync Queue',           icon: RefreshCw,       tint: '#64748b' },
  };
  const formatType = (t) => t
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());


  // Data types to export/import
  const DATA_TYPES = [
    'employees', 'customers', 'invoices', 'quotes', 'appointments',
    'inventory', 'expenses', 'mileageEntries', 'timeEntries', 'settings'
  ];

  // Update device storage status
  const updateDeviceStorageStatus = useCallback(() => {
    setDeviceStorageStatus(deviceStorage.getStatus());
  }, []);

  useEffect(() => {
    calculateStorageUsage();
    loadLastBackupDate();
    updateDeviceStorageStatus();
  }, [updateDeviceStorageStatus]);

  // Auto-save to device when data changes (if connected)
  useEffect(() => {
    if (deviceStorageStatus.isConnected) {
      const handleStorageChange = () => {
        // Debounce auto-save
        const timeoutId = setTimeout(async () => {
          try {
            await deviceStorage.saveToDevice();
            updateDeviceStorageStatus();
          } catch (error) {
            console.error('Auto-save failed:', error);
          }
        }, 2000);
        return () => clearTimeout(timeoutId);
      };

      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }
  }, [deviceStorageStatus.isConnected, updateDeviceStorageStatus]);

  const calculateStorageUsage = () => {
    let localStorageSize = 0;
    const itemCounts = {};

    // Calculate localStorage usage
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key) && key.startsWith('maidly_')) {
        const value = localStorage.getItem(key);
        localStorageSize += value ? value.length * 2 : 0; // UTF-16 = 2 bytes per char
        
        // Count items — only attempt to parse JSON-array collections. Skip
        // scalar keys like maidly_workspace_id / maidly_device_id which store
        // plain strings (parsing them throws a noisy console warning).
        const dataType = key.replace('maidly_', '');
        if (value && (value.startsWith('[') || value.startsWith('{'))) {
          try {
            const data = JSON.parse(value);
            if (Array.isArray(data)) {
              itemCounts[dataType] = data.length;
            }
          } catch (e) {
            console.warn(`[Storage] Could not parse ${dataType}:`, e);
          }
        }
      }
    }

    // Estimate IndexedDB (for inspections photos)
    if ('indexedDB' in window) {
      const request = indexedDB.open('maidly_inspections', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        if (db.objectStoreNames.contains('photos')) {
          const transaction = db.transaction(['photos'], 'readonly');
          const store = transaction.objectStore('photos');
          const countRequest = store.count();
          countRequest.onsuccess = () => {
            setStorageInfo(prev => ({
              ...prev,
              indexedDBUsed: countRequest.result * 500000 // Estimate ~500KB per photo
            }));
          };
        }
        db.close();
      };
    }

    setStorageInfo(prev => ({
      ...prev,
      localStorageUsed: localStorageSize,
      totalItems: itemCounts
    }));
  };

  const loadLastBackupDate = () => {
    const lastBackup = localStorage.getItem('maidly_last_backup');
    if (lastBackup) {
      setStorageInfo(prev => ({ ...prev, lastBackup: new Date(lastBackup) }));
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Device Storage Functions
  const connectDeviceFolder = async () => {
    try {
      const result = await deviceStorage.requestFolderAccess();
      if (result.success) {
        toast.success(`Connected to folder: ${result.folderName}`);
        updateDeviceStorageStatus();
        
        // Try to load existing data
        try {
          const loadResult = await deviceStorage.loadFromDevice();
          if (loadResult.success) {
            toast.info(`Found existing data from ${new Date(loadResult.savedAt).toLocaleDateString()}`);
          }
        } catch (e) {
          // No existing device-storage snapshot yet — expected on first run.
          console.debug('[storage] no prior device snapshot', e);
        }
        
        // Save current data
        await saveToDeviceStorage();
      } else {
        toast.error(result.error || 'Failed to connect folder');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to connect to folder');
      console.error(error);
    }
  };

  const saveToDeviceStorage = async () => {
    if (!deviceStorageStatus.isConnected) {
      toast.error('Please connect a folder first');
      return;
    }
    
    setIsSaving(true);
    try {
      const result = await deviceStorage.saveToDevice();
      if (result.success) {
        toast.success('Data saved to device!');
        updateDeviceStorageStatus();
        calculateStorageUsage();
      }
    } catch (error) {
      toast.error('Failed to save: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const loadFromDeviceStorage = async () => {
    if (!deviceStorageStatus.isConnected) {
      toast.error('Please connect a folder first');
      return;
    }
    
    setIsLoading(true);
    try {
      const result = await deviceStorage.importFromDevice(true);
      if (result.success) {
        // Push the merged result to the cloud before the reload's pullFromCloud
        // runs, so the merge isn't overwritten by the pre-merge cloud copy.
        try { await autoSync.pushAll(); } catch (e) { /* queued for retry */ }
        toast.success(`Merged ${result.itemsImported} items from device!`);
        calculateStorageUsage();
        setTimeout(() => window.location.reload(), 800);
      } else if (result.notFound) {
        toast.info('No saved data found on device');
      } else {
        toast.error(result.error || 'Failed to load data');
      }
    } catch (error) {
      toast.error('Failed to load: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectDeviceFolder = () => {
    deviceStorage.disconnect();
    updateDeviceStorageStatus();
    toast.info('Disconnected from device folder');
  };

  // Export all data to JSON file
  const exportData = () => {
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        appVersion: '1.0.0',
        deviceInfo: navigator.userAgent,
        data: {}
      };

      // Collect all data
      DATA_TYPES.forEach(type => {
        const data = localStorage.getItem(`maidly_${type}`);
        if (data) {
          exportData.data[type] = JSON.parse(data);
        }
      });

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maidly-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Save backup date
      localStorage.setItem('maidly_last_backup', new Date().toISOString());
      setStorageInfo(prev => ({ ...prev, lastBackup: new Date() }));

      toast.success('Backup created! Save this file to Google Drive or email it to yourself.');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  // Import data from JSON file
  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);

        if (!importedData.data) {
          toast.error('Invalid backup file format');
          return;
        }

        const itemCounts = Object.entries(importedData.data)
          .filter(([key, value]) => Array.isArray(value))
          .map(([key, value]) => `${value.length} ${key}`)
          .join(', ');

        const backupDate = importedData.exportDate 
          ? new Date(importedData.exportDate).toLocaleDateString() 
          : 'Unknown date';

        setConfirmModal({
          open: true,
          title: 'Import Backup',
          message: `Import backup from ${backupDate}?\n\nData found: ${itemCounts}\n\nYour existing data will be merged — newer records win, nothing is deleted.`,
          onConfirm: async () => {
            Object.entries(importedData.data).forEach(([type, newData]) => {
              const existingData = localStorage.getItem(`maidly_${type}`);

              if (type === 'settings') {
                const existing = existingData ? JSON.parse(existingData) : {};
                localStorage.setItem(`maidly_${type}`, JSON.stringify(mergeSettings(existing, newData)));
              } else if (Array.isArray(newData)) {
                const existing = existingData ? JSON.parse(existingData) : [];
                const { merged } = mergeRecordArrays(existing, newData);
                localStorage.setItem(`maidly_${type}`, JSON.stringify(merged));
              } else {
                localStorage.setItem(`maidly_${type}`, JSON.stringify(newData));
              }
            });

            // Push the merged result to the cloud BEFORE reloading, otherwise
            // the reload's pullFromCloud would overwrite the merge with the
            // pre-merge cloud copy.
            try { await autoSync.pushAll(); } catch (e) { /* queued for retry */ }

            toast.success('Data imported and merged successfully!');
            calculateStorageUsage();
            setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
            setTimeout(() => window.location.reload(), 800);
          }
        });
      } catch (error) {
        console.error('Import error:', error);
        toast.error('Failed to import data. Invalid file format.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IconTile icon={HardDrive} tint={TINT.storage} size={34} />
          Storage &amp; Backup
        </h1>
        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
          Manage your data, create backups, and sync between devices
        </p>
      </div>

      {/* Storage Overview */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <IconTile icon={Database} tint={TINT.dashboard} size={38} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
              Storage Overview
            </h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
              What&apos;s stored on this device.
            </p>
          </div>
        </div>

        {/* Stat tiles */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.85rem',
          }}
        >
          <StatTile
            tint={TINT.dashboard}
            icon={HardDrive}
            label="Local Storage"
            value={formatBytes(storageInfo.localStorageUsed)}
            hint="of ~5 MB browser limit"
          />
          <StatTile
            tint={TINT.inspections}
            icon={Camera}
            label="Photos (IndexedDB)"
            value={formatBytes(storageInfo.indexedDBUsed)}
            hint="of ~100 MB limit"
          />
          <StatTile
            tint={storageInfo.lastBackup ? TINT.scheduling : '#eab308'}
            icon={Cloud}
            label="Last Backup"
            value={storageInfo.lastBackup ? storageInfo.lastBackup.toLocaleDateString() : 'Never'}
            hint={
              storageInfo.lastBackup
                ? storageInfo.lastBackup.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                : 'Create your first backup!'
            }
          />
        </div>

        {/* Data Breakdown */}
        {(() => {
          const entries = Object.entries(storageInfo.totalItems).map(([type, count]) => ({
            type,
            count,
            meta: DATA_META[type] || { label: formatType(type), icon: FileText, tint: '#64748b' },
          }));
          const withData = entries.filter((e) => e.count > 0).sort((a, b) => b.count - a.count);
          const empty = entries.filter((e) => e.count === 0);
          const visible = showEmpty ? [...withData, ...empty] : withData;
          if (entries.length === 0) return null;

          return (
            <div style={{ marginTop: '1.25rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.65rem',
              }}>
                <div style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: '#94a3b8',
                  letterSpacing: 0.7,
                  textTransform: 'uppercase',
                }}>
                  Your Data ({withData.length} active)
                </div>
                {empty.length > 0 && (
                  <button
                    onClick={() => setShowEmpty((v) => !v)}
                    data-testid="toggle-show-empty"
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: '#0073ea',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <ChevronDown
                      size={13}
                      style={{
                        transform: showEmpty ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s',
                      }}
                    />
                    {showEmpty ? 'Hide empty' : `Show ${empty.length} empty`}
                  </button>
                )}
              </div>

              {withData.length === 0 && !showEmpty ? (
                <div style={{
                  padding: '1.25rem',
                  textAlign: 'center',
                  color: '#94a3b8',
                  fontSize: '0.85rem',
                  background: '#f8fafc',
                  borderRadius: 10,
                  border: '1px dashed #e5e7eb',
                }}>
                  No data on this device yet.
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                    gap: '0.55rem',
                  }}
                >
                  {visible.map(({ type, count, meta }) => {
                    const dim = count === 0;
                    const Icon = meta.icon;
                    return (
                      <div
                        key={type}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '0.55rem 0.7rem',
                          background: '#ffffff',
                          border: '1px solid #eef2f7',
                          borderRadius: 10,
                          opacity: dim ? 0.5 : 1,
                        }}
                      >
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 8,
                            background: `${meta.tint}1F`,
                            color: meta.tint,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon size={14} strokeWidth={2.25} />
                        </span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{
                            fontSize: '0.78rem',
                            color: '#64748b',
                            lineHeight: 1.1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {meta.label}
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            fontWeight: 800,
                            color: '#0f172a',
                            lineHeight: 1.1,
                          }}>
                            {count.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Unified Backup & Sync Module */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <IconTile icon={Cloud} tint={TINT.dashboard} size={38} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
              Backup &amp; Sync
            </h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
              Choose how you want to keep your data safe and shared across devices.
            </p>
          </div>
        </div>

        {/* Live sync status strip — always visible */}
        {(() => {
          const isSyncing = syncStatus === 'syncing';
          const isSynced  = syncStatus === 'synced';
          const isError   = syncStatus === 'error';
          const cfg = !isOnline
            ? { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', Icon: CloudOff,
                label: 'Offline — changes are saved on this device' }
            : isSyncing
            ? { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8', Icon: RefreshCw,
                label: 'Syncing to cloud…', spin: true }
            : isSynced && syncEnabled
            ? { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534', Icon: Check,
                label: 'All changes synced' }
            : isError
            ? { bg: '#fef2f2', border: '#fecaca', color: '#dc2626', Icon: AlertCircle,
                label: 'Sync error — will retry automatically' }
            : { bg: '#f8fafc', border: '#e5e7eb', color: '#475569', Icon: Cloud,
                label: syncEnabled ? 'Ready to sync' : 'Auto-sync is off' };
          const Icon = cfg.Icon;
          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              padding: '0.7rem 1rem',
              borderRadius: 12,
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              marginBottom: '1rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <Icon size={18} color={cfg.color}
                  style={cfg.spin ? { animation: 'spin 1s linear infinite' } : {}} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: cfg.color }}>{cfg.label}</div>
                  {lastSyncTime && (
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
                      Last synced {new Date(lastSyncTime).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Mode picker — big tap-friendly cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem',
            marginBottom: '1.25rem',
          }}
        >
          {[
            { key: 'cloud',  label: 'Auto Cloud',   sub: 'Signed-in sync',      icon: Cloud,       tint: TINT.dashboard,  show: true },
            { key: 'folder', label: 'Cloud Folder', sub: 'Drive · Dropbox',     icon: FolderOpen,  tint: TINT.mileage,    show: deviceStorageStatus.isSupported },
            { key: 'file',   label: 'Manual File',  sub: 'One-time transfer',   icon: FileText,    tint: TINT.inspections, show: true },
          ].filter(t => t.show).map(t => {
            const active = syncMode === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`sync-mode-${t.key}`}
                onClick={() => setSyncMode(t.key)}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  padding: '0.9rem 1rem',
                  borderRadius: 14,
                  background: active
                    ? `linear-gradient(135deg, ${t.tint} 0%, ${t.tint}dd 100%)`
                    : '#ffffff',
                  color: active ? '#fff' : '#0f172a',
                  border: active ? '1px solid transparent' : '1px solid #e5e7eb',
                  boxShadow: active
                    ? `0 8px 22px ${t.tint}55`
                    : '0 1px 3px rgba(15,23,42,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (active) return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 10px 22px ${t.tint}22`;
                  e.currentTarget.style.borderColor = `${t.tint}55`;
                }}
                onMouseLeave={(e) => {
                  if (active) return;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.04)';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: active ? 'rgba(255,255,255,0.22)' : `${t.tint}1F`,
                    color: active ? '#fff' : t.tint,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: active ? '0 2px 6px rgba(0,0,0,0.15) inset' : 'none',
                  }}
                >
                  <Icon size={17} strokeWidth={2.25} />
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.1 }}>{t.label}</span>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      color: active ? 'rgba(255,255,255,0.85)' : '#64748b',
                      marginTop: 2,
                    }}
                  >
                    {t.sub}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Mode content */}
        {syncMode === 'cloud' && (
          <div>
            <p style={{ fontSize: '0.88rem', color: '#334155', marginTop: 0, marginBottom: '1rem' }}>
              Your data automatically syncs to the cloud whenever you&apos;re signed in. Log in on any device to pick up where you left off.
            </p>

            {/* Controls */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Auto-sync toggle chip */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.55rem 1rem',
                background: '#fff',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
              }}>
                <span style={{ fontSize: '0.85rem', color: '#0f172a', fontWeight: 700 }}>Auto-sync</span>
                <button
                  onClick={() => toggleSync(!syncEnabled)}
                  data-testid="toggle-auto-sync"
                  aria-label="Toggle auto-sync"
                  style={{
                    position: 'relative',
                    width: 44,
                    height: 24,
                    background: syncEnabled ? '#16a34a' : '#d1d5db',
                    borderRadius: 12,
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    boxShadow: syncEnabled ? '0 0 0 4px rgba(22,163,74,0.15)' : 'none',
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: 2,
                    left: syncEnabled ? 22 : 2,
                    width: 20,
                    height: 20,
                    background: '#fff',
                    borderRadius: '50%',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </button>
              </div>

              {/* Sync Now — PRIMARY */}
              <button
                onClick={forceSync}
                disabled={!isOnline || syncStatus === 'syncing'}
                data-testid="sync-now-btn"
                style={{
                  padding: '0.65rem 1.25rem',
                  background: isOnline && syncStatus !== 'syncing'
                    ? 'linear-gradient(135deg, #0073ea 0%, #005bb5 100%)'
                    : '#e5e7eb',
                  color: isOnline && syncStatus !== 'syncing' ? '#fff' : '#9ca3af',
                  border: 'none',
                  borderRadius: 999,
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  cursor: isOnline && syncStatus !== 'syncing' ? 'pointer' : 'not-allowed',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: isOnline && syncStatus !== 'syncing'
                    ? '0 6px 16px rgba(0,115,234,0.35)'
                    : 'none',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isOnline || syncStatus === 'syncing') return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 24px rgba(0,115,234,0.5)';
                  e.currentTarget.style.filter = 'brightness(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isOnline && syncStatus !== 'syncing'
                    ? '0 6px 16px rgba(0,115,234,0.35)' : 'none';
                  e.currentTarget.style.filter = 'none';
                }}
              >
                <RefreshCw size={15}
                  style={syncStatus === 'syncing' ? { animation: 'spin 1s linear infinite' } : {}} />
                Sync Now
              </button>

              {/* Load from Cloud — SECONDARY (ghost) */}
              <button
                onClick={forceLoadFromCloud}
                disabled={!isOnline || syncStatus === 'syncing'}
                data-testid="load-from-cloud-btn"
                style={{
                  padding: '0.6rem 1.15rem',
                  background: '#ffffff',
                  color: isOnline ? '#0f172a' : '#9ca3af',
                  border: '1px solid #e5e7eb',
                  borderRadius: 999,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: isOnline ? 'pointer' : 'not-allowed',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isOnline) return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(15,23,42,0.10)';
                  e.currentTarget.style.borderColor = '#0073ea55';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <Cloud size={15} color={isOnline ? '#0073ea' : '#9ca3af'} />
                Load from Cloud
              </button>
            </div>
          </div>
        )}

        {syncMode === 'folder' && deviceStorageStatus.isSupported && (
          <div>
            <p style={{ fontSize: '0.88rem', color: '#334155', marginTop: 0, marginBottom: '1rem' }}>
              Save data into a cloud-synced folder (Google Drive, Dropbox, OneDrive, or iCloud) on this device — it&apos;ll appear automatically on your other devices.
            </p>

            {/* Cloud provider chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              {['Google Drive', 'Dropbox', 'OneDrive', 'iCloud'].map(p => (
                <span key={p} style={{
                  padding: '0.35rem 0.75rem',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 999,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#475569',
                }}>
                  {p}
                </span>
              ))}
            </div>

            {!deviceStorageStatus.isConnected ? (
              <div style={{
                padding: '1.25rem',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                borderRadius: 12,
                border: '1px solid #93c5fd',
                textAlign: 'center',
              }}>
                {deviceStorageStatus.hasStoredFolder && (
                  <p style={{ fontSize: '0.85rem', color: '#1e40af', marginBottom: '0.75rem' }}>
                    Previously connected to: <strong>{deviceStorageStatus.folderName}</strong>
                  </p>
                )}
                <button
                  onClick={connectDeviceFolder}
                  data-testid="connect-device-folder"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#0073ea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 4px 12px rgba(0,115,234,0.3)',
                  }}
                >
                  <FolderOpen size={18} />
                  {deviceStorageStatus.hasStoredFolder ? 'Reconnect Folder' : 'Choose Cloud Folder'}
                </button>
                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.75rem' }}>
                  Pick a folder inside your Google Drive / Dropbox / OneDrive / iCloud folder.
                </p>
              </div>
            ) : (
              <div style={{
                padding: '1rem',
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                borderRadius: 12,
                border: '1px solid #86efac',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <CheckCircle size={20} color="#16a34a" />
                  <div>
                    <span style={{ fontWeight: 700, color: '#166534' }}>Connected: {deviceStorageStatus.folderName}</span>
                    {deviceStorageStatus.lastSave && (
                      <p style={{ fontSize: '0.75rem', color: '#15803d', margin: '0.25rem 0 0' }}>
                        Last saved {new Date(deviceStorageStatus.lastSave).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={saveToDeviceStorage}
                    disabled={isSaving}
                    data-testid="save-device-now"
                    style={{
                      padding: '0.55rem 0.9rem',
                      background: '#16a34a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      cursor: isSaving ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      opacity: isSaving ? 0.7 : 1,
                    }}
                  >
                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {isSaving ? 'Saving…' : 'Save Now'}
                  </button>
                  <button
                    onClick={loadFromDeviceStorage}
                    disabled={isLoading}
                    data-testid="load-device-now"
                    style={{
                      padding: '0.55rem 0.9rem',
                      background: '#0073ea',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      cursor: isLoading ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                      opacity: isLoading ? 0.7 : 1,
                    }}
                  >
                    {isLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {isLoading ? 'Loading…' : 'Load from Folder'}
                  </button>
                  <button
                    onClick={disconnectDeviceFolder}
                    data-testid="disconnect-device"
                    style={{
                      padding: '0.55rem 0.9rem',
                      background: '#fff',
                      color: '#64748b',
                      border: '1px solid #e5e7eb',
                      borderRadius: 999,
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <XCircle size={14} />
                    Disconnect
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {syncMode === 'file' && (
          <div>
            <p style={{ fontSize: '0.88rem', color: '#334155', marginTop: 0, marginBottom: '1rem' }}>
              Download all your data as one <strong>.json</strong> file, then import it on another device. Free, no account, works anywhere.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.85rem' }}>
              {/* Export */}
              <div style={{
                padding: '1.1rem',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                borderRadius: 12,
                border: '1px solid #93c5fd',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
                  <IconTile icon={Download} tint={TINT.dashboard} size={34} />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>Export Backup</h3>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#0369a1' }}>Save your data as a file</p>
                  </div>
                </div>
                <button
                  onClick={exportData}
                  data-testid="export-backup-btn"
                  style={{
                    width: '100%',
                    padding: '0.7rem',
                    background: '#0073ea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    boxShadow: '0 4px 12px rgba(0,115,234,0.3)',
                  }}
                >
                  <Download size={16} />
                  Create Backup File
                </button>
              </div>

              {/* Import */}
              <div style={{
                padding: '1.1rem',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                borderRadius: 12,
                border: '1px solid #86efac',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
                  <IconTile icon={FolderUp} tint={TINT.inspections} size={34} />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>Import Backup</h3>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#166534' }}>Restore from a file</p>
                  </div>
                </div>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json"
                  onChange={importData}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => importFileRef.current?.click()}
                  data-testid="import-backup-btn"
                  style={{
                    width: '100%',
                    padding: '0.7rem',
                    background: '#16a34a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: '0.88rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    boxShadow: '0 4px 12px rgba(22,163,74,0.3)',
                  }}
                >
                  <FolderUp size={16} />
                  Import Backup File
                </button>
              </div>
            </div>
          </div>
        )}

        {/* "Which should I use?" collapsible helper */}
        <button
          onClick={() => setShowHelp(v => !v)}
          data-testid="sync-help-toggle"
          style={{
            all: 'unset',
            cursor: 'pointer',
            marginTop: '1.25rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: '0.78rem',
            fontWeight: 600,
            color: '#0073ea',
          }}
        >
          <ChevronDown size={14}
            style={{ transform: showHelp ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
          Which one should I use?
        </button>
        {showHelp && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.9rem 1rem',
            background: '#f8fafc',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            fontSize: '0.82rem',
            color: '#475569',
            lineHeight: 1.55,
          }}>
            <div style={{ marginBottom: '0.4rem' }}>
              <strong style={{ color: '#0f172a' }}>Auto Cloud</strong> — Best for most users. Sign in on any device and your data is there.
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <strong style={{ color: '#0f172a' }}>Cloud Folder</strong> — Best if you already use Google Drive/Dropbox and want zero-account sync between your own devices.
            </div>
            <div>
              <strong style={{ color: '#0f172a' }}>Manual File</strong> — Best for one-time transfers, sharing with an accountant, or as an emergency backup you keep offline.
            </div>
          </div>
        )}
      </div>


      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
      />
    </div>
  );
};

export default Storage;

// ---- Small helper: a big stat tile with icon chip, value and hint ----
const StatTile = ({ tint, icon: Icon, label, value, hint }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '0.95rem 1rem',
      background: '#ffffff',
      border: '1px solid #eef2f7',
      borderRadius: 12,
      boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
    }}
  >
    <span
      style={{
        width: 42,
        height: 42,
        borderRadius: 11,
        background: `${tint}1F`,
        color: tint,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={19} strokeWidth={2.25} />
    </span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1, marginTop: 2 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>{hint}</div>
      )}
    </div>
  </div>
);
